import SetItemBulk from "components/itemBulk/SetItemBulk";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useAllAgitems } from "http-hooks/agitem/useAgitem";
import {
  SelectBrandAndSubbrands,
  SelectHsn,
  SelectUomConversion,
  SelectUnits,
  SelectRawItems,
} from "components/ui/selectors";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
  ActionButton,
} from "components/ui/Table";
import Pagination from "components/ui/Pagination";
import Modal from "components/ui/modals";

export default function ItemMasterBulk() {
  const navigate = useNavigate();
  const [isSetModalOpen, setIsSetModalOpen] = useState(false);

  // Filter states using standardized selectors
  const [selectedBrandSubbrand, setSelectedBrandSubbrand] = useState(null);
  const [selectedHsn, setSelectedHsn] = useState(null);
  const [selectedUomConversion, setSelectedUomConversion] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedRawItem, setSelectedRawItem] = useState(null);

  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
  });



  const openSetModal = () => {
    setIsSetModalOpen(true);
  };

  const handleEdit = (id) => {
    navigate(`/item-master-bulk/edit/${id}`);
  };



  // Build filter object from selected values
  const buildFilter = () => {
    const filter = {
      page: 1,
      limit: 10,
    };

    if (selectedBrandSubbrand) {
      if (selectedBrandSubbrand.level === 1) {
        filter.brand = selectedBrandSubbrand.brandname;
      } else {
        filter.subbrandname = selectedBrandSubbrand.brandname;
      }
    }

    if (selectedHsn) {
      filter.itmhsnId = selectedHsn.hsncode;
    }

    if (selectedUomConversion) {
      filter.itemuom = selectedUomConversion.itemuom;
      filter.itmbasicuom = selectedUomConversion.itembasicuom;
    }

    if (selectedUnit) {
      filter.itmastuom = selectedUnit.unitname;
    }

    if (selectedRawItem) {
      filter.itemCode = selectedRawItem.item_code;
      filter.itemName = selectedRawItem.item_name;
    }

    return filter;
  };

  const clearFilters = () => {
    setSelectedBrandSubbrand(null);
    setSelectedHsn(null);
    setSelectedUomConversion(null);
    setSelectedUnit(null);
    setSelectedRawItem(null);
    setFilter({
      page: 1,
      limit: 10,
    });
  };

  const {
    isLoading: isAllAgitemsLoading,
    data: { data: allAgitems, page: currentPage, totalPages } = [],
    refetch,
  } = useAllAgitems(filter);

  // Update filter when selector values change
  useEffect(() => {
    setFilter(buildFilter());
  }, [selectedBrandSubbrand, selectedHsn, selectedUomConversion, selectedUnit, selectedRawItem]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {isSetModalOpen && (
        <Modal
          open={isSetModalOpen}
          setOpen={(val) => setIsSetModalOpen(val)}
          maxWidth="6xl"
        >
          <SetItemBulk onClose={() => setIsSetModalOpen(false)} />
        </Modal>
      )}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            Item Master(Bulk)
          </h1>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => openSetModal()}
            className="block rounded-md bg-gray-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
          >
            Add item master(bulk)
          </button>
        </div>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-lg p-3">
          <SelectBrandAndSubbrands
            value={selectedBrandSubbrand}
            onChange={setSelectedBrandSubbrand}
            label="Brand/Sub-brand"
            placeholder="Select brand/sub-brand"
            filters={{ ag_status: "1" }}
          />
        </div>

        <div className="bg-white rounded-lg p-3">
          <SelectHsn
            value={selectedHsn}
            onChange={setSelectedHsn}
            label="HSN"
            placeholder="Select HSN..."
          />
        </div>

        <div className="bg-white rounded-lg p-3">
          <SelectUomConversion
            value={selectedUomConversion}
            onChange={setSelectedUomConversion}
            label="UOM Conversion"
            placeholder="Select UOM conversion..."
          />
        </div>

        <div className="bg-white rounded-lg p-3">
          <SelectUnits
            value={selectedUnit}
            onChange={setSelectedUnit}
            label="Unit"
            placeholder="Select unit..."
          />
        </div>

        <div className="bg-white rounded-lg p-3">
          <SelectRawItems
            value={selectedRawItem}
            onChange={setSelectedRawItem}
            label="Raw Item"
            placeholder="Select raw item..."
          />
        </div>

        <div className="bg-white rounded-lg p-3 flex items-end">
          <button
            type="button"
            onClick={clearFilters}
            className="w-full rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-300"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 flow-root">
        <Table>
          <THead>
            <TR>
              <TH first>Item Id</TH>
              <TH>Brand</TH>
              <TH>Sub Brand</TH>
              <TH>Item Code</TH>
              <TH>Item Name</TH>
              <TH>HSN Id</TH>
              <TH>UOM Conversion</TH>
              <TH>UOM</TH>
              <TH>Show Brand</TH>
              <TH>Show Sub Brand</TH>
              <TH>Status</TH>
              <TH last>
                <span className="sr-only">Actions</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {isAllAgitemsLoading ? (
              <LoadingRow colSpan={12} message="Loading items..." />
            ) : allAgitems?.length === 0 ? (
              <EmptyRow colSpan={12} message="No items found." />
            ) : (
              allAgitems?.map((agitem) => (
                <TR key={agitem.itemId}>
                  <TD first>{agitem.itemId}</TD>
                  <TD>{agitem.brand}</TD>
                  <TD>{agitem.subbrandname}</TD>
                  <TD>{agitem.itemCode}</TD>
                  <TD>{agitem.itemName}</TD>
                  <TD>{agitem.itmhsnId}</TD>
                  <TD>
                    {"1 " +
                      agitem.itemuom +
                      " = " +
                      agitem.aguomconval +
                      " " +
                      agitem.itmbasicuom}
                  </TD>
                  <TD>{agitem.itmastuom}</TD>
                  <TD>
                    {agitem.boolbrandshow === 1 ? (
                      <CheckIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                    )}
                  </TD>
                  <TD>
                    {agitem.boolsubbrandshow === 1 ? (
                      <CheckIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                    )}
                  </TD>
                  <TD>
                    {agitem.ag_status === 1 ? (
                      <CheckIcon className="h-5 w-5 text-green-600" aria-hidden="true" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                    )}
                  </TD>
                  <TD last>
                    <ActionButton
                      variant="secondary"
                      onClick={() => handleEdit(agitem.itemId)}
                      title="Edit Item"
                      className="mr-0"
                    >
                      <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Edit item, {agitem.itemId}</span>
                    </ActionButton>

                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => setFilter((prev) => ({ ...prev, page }))}
        showPageJump={true}
      />
    </div>
  );
}
